import { Network } from '@capacitor/network';
import { useEffect, useState } from 'react';
import { Route, HashRouter, BrowserRouter, Routes, useLocation, useNavigate } from 'react-router-dom';
import 'react-tooltip/dist/react-tooltip.css';
import './App.css';
import './assets/css/animate.min.css';
import './assets/css/style.css';
import './assets/scss/style.scss';
import Layout from './components/layouts/Layout';
import PrivateLayout from './components/layouts/PrivateLayout';
import { Config } from './config/Config';
import { privateRoutes, publicRoutes, Routes as AppRoutes } from './constants/RouteConstants';
import ContactUsPublic from './pages/cms-public/ContactUs';
import PrivacyPolicyPublic from './pages/cms-public/PrivacyPolicy';
import Home from './pages/home/Home';
import OfflinePage from './pages/others/OfflinePage';
import PageNotFound from './pages/others/PageNotFound';
import PrivateRoute from './router/PrivateRoutes';
import { initializePushNotifications } from './config/pushNotifications';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { saveToStorage } from './utils/Helper';

const OnboardingHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const skipOnboarding = queryParams.get('skip_onboarding');

    if (skipOnboarding?.toLowerCase() === 'true') {
      saveToStorage({ key: 'is_intro_viewed', value: true });
      if (location.pathname !== AppRoutes?.Login) {
        navigate(AppRoutes?.Login, { replace: true });
      }
    }
  }, [location.search, location.pathname, navigate]);

  return null;
};

const App = () => {

  const [isNetworkConnected, setIsNetworkConnected] = useState(true)
  const [isAppReady, setIsAppReady] = useState(false);


  useEffect(() => {
    setIsAppReady(true);
  }, []);

  const isNativeApp = Capacitor.isNativePlatform();

  useEffect(() => {
    if (isNativeApp) {
      document.body.classList.add('is-native-app');
    }
  }, [isNativeApp]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        document.body.classList.add('using-keyboard');
      }
    };

    const handlePointerDown = () => {
      document.body.classList.remove('using-keyboard');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('touchstart', handlePointerDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);


  useEffect(() => {
    if (!isAppReady) return;

    const startApp = async () => {
      if (Capacitor.isNativePlatform()) {
        // Give one frame for UI paint
        requestAnimationFrame(async () => {
          await SplashScreen.hide();

          // Now safe to ask permission
          setTimeout(async () => {
            await initializePushNotifications();
          }, 500);
        });
      } else {
        await initializePushNotifications();
      }
    };

    startApp();
  }, [isAppReady]);


  useEffect(() => {
    const handleNetworkChange = (status: any) => {
      setIsNetworkConnected(status?.connected ?? true);
    };

    Network.getStatus().then(handleNetworkChange);
    Network.addListener('networkStatusChange', handleNetworkChange);
    return () => {
      Network.removeAllListeners();
    };
  }, []);


  const RouterComponent = Capacitor.isNativePlatform()
    ? HashRouter
    : BrowserRouter;

  return (
    <>
      <RouterComponent basename='/'>
        <OnboardingHandler />
        {
          isNetworkConnected === false ?
            <OfflinePage />
            :
            <Routes>
              <Route element={<Layout />}>
                {
                  publicRoutes?.map((route, routeKey) => {
                    return <Route path={route?.path} element={route?.element} key={routeKey} />
                  })
                }
                <Route element={<PrivateRoute component={PrivateLayout} />}>
                  {
                    privateRoutes?.map((route, routeKey) => {
                      return <Route path={route?.path} element={route?.element} key={routeKey} />
                    })
                  }
                </Route>
                <Route path={'*'} element={<PageNotFound />} />
              </Route>
              <Route path={'/privacy-policy'} element={<PrivacyPolicyPublic />} />
              <Route path={'/contactus'} element={<ContactUsPublic />} />
              <Route path={'/'} element={<Home />} />
            </Routes>
        }
      </RouterComponent>
    </>
  );
}

export default App;